/**
 * Journey: IUI (Intrauterine Insemination), With Partner, Male
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * Same 6-step structure as the female IUI journey, reframed for the male partner.
 * The male provides the sperm sample; the female partner undergoes monitoring,
 * trigger, and insemination. Content is written from the male's perspective.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IUI Works",
  subtitle: "A less invasive fertility treatment",
  icon: "BookOpen",
  education: {
    context:
      "IUI (intrauterine insemination) places washed, concentrated sperm directly into your partner's uterus around ovulation. It's less invasive and less expensive than IVF, and is often the first treatment recommended.",
    details:
      "The process:\n1. Monitoring (days 1-12) — Your partner has ultrasounds and blood work to track follicle growth\n2. Trigger shot (day 12-14) — An injection triggers ovulation 36 hours later\n3. Your sperm sample — You provide a fresh sample at the clinic on insemination day\n4. Insemination (15-20 minutes) — Washed, concentrated sperm is placed via a thin catheter\n5. Two-week wait — You both wait ~14 days for a pregnancy test\n\nWith medications (Clomid or Letrozole), success rates are 10-20% per cycle. Most clinics recommend 3-4 IUI cycles before considering IVF.",
    learnMoreLink: "iui-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IUI guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "iui-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "male-factor",
      additionalEducation:
        "Your semen analysis suggests male factor infertility. IUI can help with mild male factor by concentrating the healthiest sperm. If your post-wash total motile count is below 5 million, your clinic may recommend IVF with ICSI instead.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 39],
      content:
        "Male age has a modest effect on IUI outcomes. Sperm quality is generally well-maintained through your 30s.",
    },
    {
      range: [40, 44],
      content:
        "After 40, sperm DNA fragmentation increases slightly. Your clinic may recommend a DNA fragmentation test.",
    },
    {
      range: [45, 99],
      content:
        "Over 45, sperm quality declines. Your clinic may discuss DNA fragmentation testing and whether IVF with ICSI would be more effective than IUI.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Baseline Testing",
  subtitle: "Confirm IUI is right for you both",
  icon: "TestTube",
  education: {
    context:
      "Before starting IUI, your clinic needs your semen analysis (to confirm sperm parameters are suitable for IUI) and your partner's tests (open tubes, adequate ovarian reserve). These results help decide if IUI is a good fit or if IVF would be more effective.",
  },
  actions: [
    {
      label: "Find a lab near you",
      variant: "primary",
      screen: "FindCareScreen",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "semen-analysis",
      title: "Semen analysis",
      education:
        "Measures count, motility, and morphology. Post-wash total motile count above 5-10 million is ideal for IUI. Abstain 2-5 days before your appointment.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required for both partners before any fertility treatment. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  partnerTrack: {
    education:
      "Your partner's tests confirm that IUI is viable — open fallopian tubes and adequate ovarian reserve are required.",
    substeps: [
      {
        id: "partner-amh",
        title: "Partner: AMH (Anti-Müllerian Hormone)",
        education:
          "Measures egg supply. Helps the clinic choose the right medication protocol.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-fsh-estradiol",
        title: "Partner: FSH + Estradiol",
        education: "Cycle day 2-3 blood test. Shows ovarian function.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-hsg",
        title: "Partner: HSG (Hysterosalpingogram)",
        education:
          "Confirms at least one fallopian tube is open — this is required for IUI to work.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-infectious",
        title: "Partner: Infectious disease screening",
        education: "Required for both partners. Standard panel.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: { type: "all-substeps" },
  optimizationWindow: {
    substepId: "semen-analysis",
    biologicalCycle: "~74 days for full sperm regeneration",
    baselineAction: "Get a baseline semen analysis",
    optimizationPeriod: "2-3 months",
    retestAction: "Retest to see improvement",
    lifestyle: [
      {
        title: "Avoid excessive heat",
        evidence: "strong",
        impact: "high",
        education:
          "Sperm production requires temperatures 2-4°C below body temperature. Hot tubs, saunas, tight underwear, and laptops on your lap all raise scrotal temperature.",
      },
      {
        title: "Limit alcohol",
        evidence: "strong",
        impact: "moderate",
        education:
          "Heavy alcohol use reduces testosterone and impairs sperm production. Limiting intake during your optimization window is prudent.",
      },
      {
        title: "Exercise regularly",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Regular moderate exercise improves sperm parameters. Avoid extreme endurance exercise and heavy cycling, which can temporarily lower counts.",
      },
      {
        title: "Quit smoking/vaping",
        evidence: "strong",
        impact: "high",
        education:
          "Tobacco reduces sperm count by 20-30% and increases DNA fragmentation. Cannabis also impairs motility and morphology.",
      },
    ],
    supplements: [
      {
        title: "CoQ10 (200-400mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports mitochondrial function in sperm cells. Multiple studies show improvements in count and motility after 3 months.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Zinc (30mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Essential for testosterone production and sperm development. Seminal fluid has 100x the zinc concentration of blood.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Folate/L-methylfolate (400-800mcg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports DNA synthesis in developing sperm. Combined with zinc, studies show improved count.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
    ],
    ageGuidance: [
      {
        bracket: "Under 40",
        range: [0, 39],
        recommendation: "optimize-then-start",
        explanation:
          "You have time to optimize. A 2-3 month window of supplements and lifestyle changes can improve your post-wash motile count, which directly affects IUI success.",
      },
      {
        bracket: "40-45",
        range: [40, 45],
        recommendation: "parallel",
        explanation:
          "Start optimizing now while your partner begins her testing. The timing aligns well — her workup takes 1-2 months too.",
      },
      {
        bracket: "Over 45",
        range: [46, 99],
        recommendation: "start-now-optimize-alongside",
        explanation:
          "Don't delay your partner's cycle for optimization. Start supplements and lifestyle changes now, but proceed with IUI on your partner's timeline.",
      },
    ],
  },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Most REIs offer IUI",
  icon: "Building2",
  education: {
    context:
      "Most reproductive endocrinology clinics offer IUI. It's a simpler procedure than IVF, so clinic success rate differences are less dramatic — but experience still matters. You'll both attend the initial consultation.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "iui" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education: "Look for clinics with IUI experience and good reviews.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education:
        "Initial consultation to review both of your results and confirm IUI is appropriate.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education:
        "Your clinic will start the onboarding process for both of you.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Pre-cycle setup",
  icon: "ClipboardCheck",
  education: {
    context:
      "IUI prep is simpler than IVF. Your clinic will confirm the protocol (natural vs. medicated for your partner), and you'll both complete medical history and consent forms.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "medical-history",
      title: "Complete your medical history",
      education: "Your health history is needed for consent forms.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education:
        "Needed for billing. IUI is more likely to be covered than IVF.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Both partners sign. Covers procedure, risks, and multiple pregnancy risk.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements for sperm quality (optional)",
      education:
        "CoQ10, zinc, and folate support sperm quality. Start 2-3 months before if possible — sperm takes ~74 days to regenerate.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  partnerTrack: {
    education:
      "Your partner's medical history and medication protocol are managed by the clinic.",
    substeps: [
      {
        id: "partner-medical-history",
        title: "Partner: complete medical history",
        education: "Needed for her medication protocol and consent forms.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-medication-protocol",
        title: "Partner: confirm medication protocol",
        education:
          "The clinic will prescribe Clomid or Letrozole (oral), or injectable gonadotropins for stronger stimulation.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IUI Cycle",
  subtitle: "Monitoring, trigger, and insemination",
  icon: "Syringe",
  education: {
    context:
      "Your partner's IUI cycle is active. Track appointments and supporting actions in the Care section.",
    details:
      "A typical IUI cycle unfolds over 2-3 weeks:\n\n• Day 3 — Your partner starts cycle medications\n• Days 8-12 — Monitoring visits (ultrasound + bloodwork)\n• Trigger — Shot when follicles are ready, precisely timed\n• Insemination — 36 hours after trigger; you provide a fresh sperm sample, which is prepared and placed in her uterus\n• Two-week wait — Before the pregnancy blood test\n\nYour key moment: providing a fresh sample at the clinic on insemination day. Abstain 2-5 days before.\n\nYour Care page has the shared appointment calendar.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between insemination and pregnancy test",
  icon: "Clock",
  education: {
    context:
      "After IUI, you both wait about 14 days for a pregnancy blood test.",
    details:
      "What's happening:\nDays 1-3: Sperm meets egg in the fallopian tube, fertilization occurs\nDays 4-6: The fertilized egg travels to the uterus and begins implanting\nDays 7-10: Implantation completes, hCG starts being produced\nDays 11-14: hCG rises to detectable levels\n\nHow you can help:\n• Be patient — your partner may be anxious, emotional, or symptom-spotting. All of that is normal\n• Don't read too much into symptoms — they can mean pregnant OR not pregnant\n• Help with practical things — meals, errands, keeping life low-stress\n• Home tests before day 12 are unreliable — wait for the blood test\n\nIf this cycle doesn't work, most clinics recommend 3-4 IUI cycles before considering IVF.",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "When is the blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IUI_PARTNER_MALE: JourneyDefinition = {
  key: "iui-with-partner-male",
  gender: "male",
  goal: "pregnancy",
  pathway: "with-partner",
  title: "IUI",
  description: "Intrauterine insemination with your partner",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
